import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <header className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 z-10">
        <button 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
      </header>
      
      <main className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-slate-500 mb-8">Last updated: February 2026</p>
        
        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-slate-700 mt-4 mb-2">Personal Information</h3>
            <p className="text-slate-600 mb-4">When you create an account, we collect:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Name and email address</li>
              <li>Profile picture (if provided via Google login)</li>
              <li>Travel preferences from onboarding quiz</li>
              <li>Payment information (processed securely by Stripe)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-slate-700 mt-4 mb-2">Usage Data</h3>
            <p className="text-slate-600 mb-4">We automatically collect:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Device information and browser type</li>
              <li>IP address and general location</li>
              <li>Pages visited and features used</li>
              <li>Savings goals and transaction history</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">2. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use your information to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide and improve the Service</li>
              <li>Personalize your experience and recommendations</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send important updates and notifications</li>
              <li>Respond to your inquiries and support requests</li>
              <li>Prevent fraud and ensure security</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">3. Information Sharing</h2>
            <p className="text-slate-600 mb-4">We do not sell your personal information. We may share data with:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Service Providers:</strong> Stripe (payments), MongoDB (database), Resend (emails)</li>
              <li><strong>Travel Partners:</strong> Amadeus (flights/hotels) for deal searches</li>
              <li><strong>AI Services:</strong> OpenAI for travel planning features</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect rights</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">4. Data Security</h2>
            <p className="text-slate-600 mb-4">
              We implement appropriate security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing</li>
              <li>Regular security audits</li>
              <li>Limited access to personal data</li>
            </ul>
            <p className="text-slate-600 mt-4">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee 
              absolute security of your data.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">5. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Update inaccurate information</li>
              <li><strong>Deletion:</strong> Delete your account and associated data</li>
              <li><strong>Export:</strong> Download your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p className="text-slate-600 mt-4">
              To exercise these rights, visit Settings → Export My Data or Settings → Delete Account, 
              or contact us at privacy@savecation.app.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">6. Cookies and Tracking</h2>
            <p className="text-slate-600 mb-4">We use cookies and similar technologies for:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Essential:</strong> Authentication and session management</li>
              <li><strong>Functional:</strong> Remembering your preferences</li>
              <li><strong>Analytics:</strong> Understanding how you use the Service</li>
            </ul>
            <p className="text-slate-600 mt-4">
              You can manage cookie preferences in your browser settings. Disabling essential cookies 
              may affect Service functionality.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">7. Data Retention</h2>
            <p className="text-slate-600 mb-4">
              We retain your personal information for as long as your account is active or as needed 
              to provide the Service. After account deletion:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Personal data is deleted within 30 days</li>
              <li>Anonymized usage data may be retained for analytics</li>
              <li>Financial records are kept as required by law</li>
            </ul>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">8. Children's Privacy</h2>
            <p className="text-slate-600 mb-4">
              The Service is not intended for users under 18 years of age. We do not knowingly collect 
              personal information from children. If you believe a child has provided us with personal 
              information, please contact us.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">9. International Transfers</h2>
            <p className="text-slate-600 mb-4">
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place for such transfers.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">10. Changes to This Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this Privacy Policy periodically. We will notify you of significant changes 
              via email or in-app notification. The "Last updated" date indicates when the policy was 
              last revised.
            </p>
          </section>
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">11. Contact Us</h2>
            <p className="text-slate-600">
              For privacy-related inquiries, contact us at:
              <br />
              <a href="mailto:privacy@savecation.app" className="text-sky-600 hover:underline">
                privacy@savecation.app
              </a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
